import { Link } from 'react-router-dom';
import { useReveal } from '../lib/anim/useReveal';
import { usePageMeta } from '../lib/usePageMeta';
import { StackDiagram } from '../components/StackDiagram';
import { StackReasoning } from '../components/StackReasoning';
import { CostTable } from '../components/CostTable';

export function Stack() {
  usePageMeta(
    'The Stack — Clarity Platform',
    'Every tool Clarity Platform is built on, and every reason it\'s there. Cloudflare, Supabase, Stripe, Resend, Anthropic — no black boxes.',
  );
  const intro = useReveal();
  const diagramReveal = useReveal({ delay: 1 });
  const footer = useReveal();

  return (
    <main className="min-h-screen text-ink" style={{ background: 'var(--color-bg)' }}>
      {/* ── Hero / Intro ── */}
      <section className="relative overflow-hidden py-28 md:py-36">
        {/* Ambient glow — accent-soft */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'var(--color-accent-soft)' }}
        />

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <p
            className="text-sm uppercase tracking-widest mb-5 font-medium"
            style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}
          >
            Transparency
          </p>

          <div ref={intro.ref} {...intro.props} className={intro.props.className}>
            <h1
              className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight text-ink"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              The stack
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-normal" style={{ color: 'var(--color-mute)' }}>
              Every tool Onwards builds on. Every reason it's there.
            </p>
            <p className="mt-6 max-w-2xl mx-auto leading-relaxed text-base md:text-lg" style={{ color: 'var(--color-mute)' }}>
              Clarity Platform runs on a single vendor family where possible — Cloudflare for edge
              compute and storage, Supabase for Postgres, Stripe for billing, Resend for email, and
              Anthropic for AI reasoning. Fewer auth surfaces, fewer moving parts, fewer things
              that can silently fail at 2 am.
            </p>
          </div>
        </div>
      </section>

      {/* ── Architecture Diagram ── */}
      <section className="py-12 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div
            ref={diagramReveal.ref}
            {...diagramReveal.props}
            className={`rounded-2xl border p-4 sm:p-8 overflow-hidden${diagramReveal.props.className ? ' ' + diagramReveal.props.className : ''}`}
            style={{ borderColor: 'var(--color-hairline)', background: 'var(--color-surface)' }}
          >
            <StackDiagram />
          </div>
          <p className="mt-4 text-center text-xs" style={{ color: 'var(--color-mute)' }}>
            Customer micro-SaaS products sit on top of the Clarity Platform, which in turn sits on
            the Onwards chassis. Each dashed border is a tenant boundary enforced by Supabase RLS.
          </p>
        </div>
      </section>

      {/* ── Per-component reasoning ── */}
      <StackReasoning />

      {/* ── Cost table ── */}
      <CostTable />

      {/* ── Footer CTA ── */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div
            ref={footer.ref}
            {...footer.props}
            className={footer.props.className}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-ink leading-tight" style={{ fontFamily: 'var(--font-display)' }}>
              Build on a chassis you can trust.
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: 'var(--color-mute)' }}>
              No black boxes. No vendor lock-in you can't see. Start with Pilot and ship your
              first micro-SaaS on these rails.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup?tier=pilot"
                className="inline-flex items-center justify-center px-8 py-4 rounded font-semibold text-base hover:opacity-90 transition-opacity"
                style={{ background: 'var(--color-accent)', color: '#FFFFFF' }}
              >
                Start with Pilot — $149/mo
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-8 py-4 rounded font-medium text-base transition-colors"
                style={{ border: '1px solid var(--color-hairline)', color: 'var(--color-ink)' }}
              >
                ← Back to home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
